import { landingPathFor } from "@/lib/constants/roles";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { useAuth } from "../../contexts/AuthContext";

export function LoginForm() {
  const { redirect } = useSearch({ from: "/login" });
  const { signIn, signOut } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      setError("Please enter username and password");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const result = await signIn(username, password);

      if (result.success) {
        // Redirect based on role
        const landingPath = landingPathFor(result.user?.role);
        let redirectTo: string;
        if (redirect) {
          redirectTo = redirect;
        } else if (landingPath) {
          redirectTo = landingPath;
        } else {
          // Role has no home in this app - sign them out and show error message
          await signOut();
          redirectTo = "/login?error=role_not_allowed";
        }
        window.location.href = redirectTo;
      } else {
        setError(result.error || "Invalid username or password");
      }
    } catch (_err) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-lg bg-error-50 border border-error-200 p-3">
          <p className="text-sm text-error-600">
            {" "}
            {error === "Failed to fetch" ? "Server unavailable" : error}
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
            Username
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
            placeholder="Enter your username"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 pr-10 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <HiEyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <HiEye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="pt-1">
        <Button type="submit" disabled={isLoading} variant="primary" className="w-full">
          {isLoading ? "Signing in..." : "Sign in"}
        </Button>
      </div>
    </form>
  );
}
